import axios from 'axios';
import App from '../main';
import * as Sentry from "@sentry/vue";

const MAX_RETRY_COUNT = 3;
const CANCEL_TOKENS = {};
const main_axios_instance = axios.create();
const domain = window.rsvDomain || 'rsv.ru';

export const makeURL = window.rsvMakeURL || ((sub, path) => {
        /*
    if (sub === "examination") {
        return `http://localhost/${path}`;
    }
*/
        return `https://${sub}.${domain}/${path}`;
    });

const checkResponse = (response, full = false) => {
    if (full) return response.data;
    if (response.status === 200 && response.data.status && response.data.status.toLowerCase() === 'success') return response.data.data;

    return null;
};
const checkPostV2Response = (response, full = false) => {
    if (full) return response.data;
    if (response.status != 200 || response.data.status != 'success')
        return null;

    return response.data.data;
};

const exceptionListeners = [];

export function addExceptionListener(cb) {
    exceptionListeners.push(cb);
}

export const qs = dict => {
    return Object.keys(dict).map((key) => {
            return `${key}=${dict[key]}`;
    }).join('&');
};

const statusCodeInterceptors = {};

export const registerSecurityService = (securityService) => {
    securityService.addUserListener((user) => {
        if (user) {
            main_axios_instance.defaults.headers.common['Authorization'] = `Bearer ${user.access_token}`;
        } else {
            delete main_axios_instance.defaults.headers.common['Authorization'];
        }
    });
    statusCodeInterceptors[401] = () => securityService.checkAuth({ requiresAuth: true });
};

export const get = async (sub, path, params, full = false, notify = true) => {
    const url = makeURL(sub, path);
    const response = await main_axios_instance.get(url, { params, notify });

    return checkResponse(response, full);
};

export const delete_request = async (sub, path, params, full = false, notify = true) => {
    const url = makeURL(sub, path);

    const response = await main_axios_instance.delete(url, { params, notify });

    return checkResponse(response, full);
};

export const del = async (sub, path, { params = null, data = null } = {}, full = false, notify = true) => {
    const url = makeURL(sub, path);
    const response = await main_axios_instance.delete(url, {
        params,
        data,
        notify,
    });
    return checkResponse(response, full);
};

export const get_projectory = async (sub, isToken) => { // "serve": "vue-cli-service serve --port 3000" for projectoria before 01.06.2020. After 01.06.2020 projectoria will remove 3000 port from cors
    const token = main_axios_instance.defaults.headers.common['X-AUTH-TOKEN'];
    const url = `https://edu-lb.proektoria.online/api/v1/tests/${sub}?${isToken ? `token=${token}` : ''}`;
    const response = await main_axios_instance.get(url);
    const data = response.data;
    if (response.status !== 200 || data) {
        return data;
    } else {
        setTimeout(await get_projectory(sub, isToken), 5000);
    }
};

export const post = async (
    sub,
    path,
    body = {},
    isJSON = false,
    headers = {},
    full = false,
    notify = true,
    responseType = undefined
) => {
    const url = makeURL(sub, path);
    const reducer = (acc, cur) => {
        if (Array.isArray(body[cur]))
            body[cur].forEach(v => acc.append(`${cur}[]`, v));

        else acc.append(cur, body[cur]);

        return acc;
    };
    const encoded = isJSON
        ? body
        : Object.keys(body).reduce(reducer, new FormData);

    try {
        // const response = await main_axios_instance.post(url, encoded);
        const response = await main_axios_instance({
            method: 'POST',
            url: url,
            data: encoded,
            responseType,
            headers: { ...main_axios_instance.defaults.headers.common, ...headers },
            notify,
        });

        return checkResponse(response, full);
    } catch (e) {
        return null;
    }
};

export const postV2 = async (
    sub,
    path,
    body = {},
    isJSON = false,
    { full = false, nullOnError = true },
    headers = {},
    notify = true
) => {
    const url = makeURL(sub, path);

    const reducer = (acc, cur) => {
        if (Array.isArray(body[cur]))
            body[cur].forEach(v => acc.append(`${cur}[]`, v));

        else acc.append(cur, body[cur]);

        return acc;
    };

    const encoded = isJSON
        ? body
        : Object.keys(body).reduce(reducer, new FormData);

    const request = {
        method: 'POST',
        url,
        data: encoded,
        headers: { ...main_axios_instance.defaults.headers.common, ...headers },
        notify,
    };

    let res;
    try {
        res = await axios(request);
    } catch (e) {
        exceptionListeners.forEach((cb) => {
            cb(e.response);
        });
        if (!nullOnError) {
            let result = e.response.data;
            if (!full) {
                result = result.data;
            }
            throw result;
        }
        return null;
    }

    return checkPostV2Response(res, full);
};

export const patch = async (sub, path, body = {}, isJSON = false, headers = {}, full = false, notify = true) => {
    const url = makeURL(sub, path);

    const reducer = (acc, cur) => {
        if (Array.isArray(body[cur]))
            body[cur].forEach(v => acc.append(`${cur}[]`, v));

        else acc.append(cur, body[cur]);

        return acc;
    };

    const encoded = isJSON
        ? body
        : Object.keys(body).reduce(reducer, new FormData);

    try {
        // const response = await main_axios_instance.post(url, encoded);
        const response = await main_axios_instance({
            method: 'PATCH',
            url: url,
            data: encoded,
            headers: { ...main_axios_instance.defaults.headers.common, ...headers },
            notify,
        });

        return checkResponse(response, full);
    } catch (e) {
        return null;
    }
};

export const lmsCourse = id => makeURL('lms', `courses/${id}/page`);

export const hrefToTest = id => `https://${domain}/testing/start/${id}`;

// Interceptors

main_axios_instance.interceptors.request.use((config) => {
        if (config.retryCount === undefined) {
            config.retryCount = 0;
        }

        return config;
    },
    (err) => {
        console.log(err);
});

main_axios_instance.interceptors.response.use(function (response) {
        if (response.data.status && response.data.status.toLowerCase() !== 'success') {
            if (checkIsAdmin() && response.config.notify) {
                App.$noty.warning(response.data.data);
            }
        }

        return response;
}, function (error) {
        Sentry.captureException(error);

        if (error.response && statusCodeInterceptors[error.response.status]) {
            console.log('[DEBUG] error.response.status', error.response.status);
        return statusCodeInterceptors[error.response.status]()
            .then(() => {
                error.config.headers['Authorization'] = main_axios_instance.defaults.headers.common['Authorization'];
                error.config.baseURL = undefined;
                console.log('[DEBUG] Retry request', error.config);

                if (error.config.retryCount < MAX_RETRY_COUNT) {
                    error.config.retryCount += 1;
                    return main_axios_instance.request(error.config);
                }

                return Promise.reject(error);
            });
        }

        if (checkIsAdmin() && error.config.notify) {
            let level, message;
            if (error.response) {
                if (error.response.status === 500) {
                    level = 'error';
                    message = 'Что-то пошло не так, попробуйте позже';
                } else {
                    level = 'warning';
                    if (error.response.data && error.response.data.message) {
                        message = error.response.data.message;
                    } else {
                        message = 'Что-то пошло не так';
                    }
                }
            } else {
                level = 'error';
                message = 'Что-то пошло не так, перезагрузите страницу или попробуйте позже';
            }

            App.$noty[level](message);
        }

        return Promise.reject(error);
});

export const checkIsAdmin = () => {
    const admins = ["ROLE_ADMIN", "ROLE_CURATOR", "ROLE_PROJECT_ADMIN", "ROLE_EXPERT"];
    if (App.$store.getters.user.roles != undefined && App.$store.getters.user.roles.length > 0)
        return admins.some(r => App.$store.getters.user.roles.indexOf(r) >= 0);
    else return false;
};

export const getJwt = () => {
    const header = main_axios_instance.defaults.headers.common['Authorization'];
    if (header && header.startsWith('Bearer ')) {
        return header.substring(7);
    }
    return "";
};

export const upload = async (sub, path, name, file, ...args) => {
    let params = {};
    let isAsync = false;
    let full = false;
    if (args.length === 1) {
      if ("boolean" === typeof args[0]) {
            isAsync = args[0];
        } else {
            params = args[0];
        }
    } else if (args.length > 1) {
        params = args[0];
      if ("boolean" === typeof args[1]) {
            isAsync = args[1];
        } else {
            isAsync = args[1].isAsync || false;
            full = args[1].full || false;
        }
    }

    const url = makeURL(sub, path);

    const formData = new FormData();
    formData.set(name, file);

    const fileUpload = {
      method: "POST",
        url,
        data: formData,
        params: params,
        headers: {
        "Content-Type": "multipart/form-data",
        },
    };
    if (isAsync) {
        return axiosApi.request(fileUpload);
    }
    try {
        const response = await axiosApi.request(fileUpload);
        return checkResponse(response, full);
    } catch (e) {
        return null;
    }
};

export const rawGet = async (sub, path, params, headers = {}, responseType = undefined, abortID = null) => {
    const url = makeURL(sub, path);

    const controllerID = `rawGet_${abortID}`;

    if (abortID) {
        if (CANCEL_TOKENS[controllerID])
            CANCEL_TOKENS[controllerID].cancel('Request is out of date. New one has been made');

        CANCEL_TOKENS[controllerID] = axios.CancelToken.source();
    }

    const response = await main_axios_instance.get(url, {
        params,
        headers,
        responseType,
        cancelToken: (CANCEL_TOKENS[controllerID] || {}).token,
    });

    if (abortID) delete CANCEL_TOKENS[controllerID];

    return response;
};
